import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-200">
      <header className="border-b border-zinc-800/60 bg-[#0a0a0a]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold tracking-tight text-white">
            Wamerce
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="text-sm text-zinc-400 hover:text-white transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="space-y-2 mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-white tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-sm text-zinc-400">
            Effective Date: 14 February 2026 · Last updated: 14 February 2026
          </p>
        </div>

        <article
          data-testid="privacy-policy-content"
          className="prose prose-invert prose-zinc max-w-none
                     prose-headings:text-white prose-headings:font-semibold
                     prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
                     prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
                     prose-p:text-zinc-300 prose-p:leading-relaxed
                     prose-li:text-zinc-300 prose-strong:text-white
                     prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline"
        >
          <p>
            This Privacy Policy explains how <strong>Wamerce</strong> (“we”, “us”, “our”) collects,
            uses, stores, shares and protects information when you use the Wamerce platform at{' '}
            <a href="https://www.wamerce.com">www.wamerce.com</a> (the “Service”). The Service is
            an e-commerce management platform for merchants and includes order management,
            logistics, marketing, and WhatsApp Business messaging features.
          </p>

          <h2>1. Information We Collect</h2>
          <h3>1.1 Information you provide</h3>
          <ul>
            <li>
              <strong>Account data:</strong> name, business name, email, phone number, password
              (hashed), billing address.
            </li>
            <li>
              <strong>Store credentials:</strong> Shopify API tokens, courier API keys, payment
              gateway keys you connect to your account (encrypted at rest).
            </li>
            <li>
              <strong>Communications:</strong> support tickets, in-app messages, feedback.
            </li>
          </ul>

          <h3>1.2 Information from your customers (you control)</h3>
          <p>
            When you use Wamerce to message <em>your</em> customers via WhatsApp, SMS or email, we
            process limited customer data on your behalf as a data processor:
          </p>
          <ul>
            <li>Customer name and phone number</li>
            <li>Order number, order amount, tracking link, fulfillment status</li>
            <li>Opt-in / opt-out status for marketing messages</li>
            <li>Message delivery and read receipts</li>
          </ul>

          <h3>1.3 Information collected automatically</h3>
          <ul>
            <li>Device, browser, IP address, time zone, referrer</li>
            <li>Pages viewed, features used, session duration (via PostHog product analytics)</li>
            <li>Cookies and local storage tokens for keeping you signed in</li>
          </ul>

          <h2>2. WhatsApp Business Platform Usage</h2>
          <p>
            Wamerce integrates with the <strong>Meta WhatsApp Business Platform (Cloud API)</strong>{' '}
            to send transactional and marketing messages on your behalf. When you connect your
            WhatsApp Business Account (WABA) to Wamerce:
          </p>
          <ul>
            <li>
              We use your <strong>Permanent Access Token</strong> only to send template messages
              you have approved with Meta, receive incoming customer replies, and receive message
              status updates (sent / delivered / read / failed).
            </li>
            <li>
              <strong>Webhook events</strong> from Meta (incoming messages, status updates, account
              alerts) are received at{' '}
              <code>https://www.wamerce.com/api/whatsapp/webhook</code> and signature-verified
              against your App Secret before being stored.
            </li>
            <li>
              We do <strong>not</strong> share WhatsApp message content with third parties, sell it
              to advertisers, or use it to train AI models without explicit opt-in.
            </li>
            <li>
              Your customers can opt out at any time by replying <strong>STOP</strong>; we will not
              send further marketing messages to that number for 12 months.
            </li>
            <li>
              All WhatsApp data is used in accordance with the{' '}
              <a
                href="https://www.whatsapp.com/legal/business-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp Business Messaging Policy
              </a>{' '}
              and the{' '}
              <a
                href="https://developers.facebook.com/terms/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Meta Platform Terms
              </a>
              .
            </li>
          </ul>

          <h2>3. How We Use Information</h2>
          <ul>
            <li>To operate, secure and improve the Service</li>
            <li>
              To send notifications you configure (order confirmation, shipping, abandoned cart,
              COD verification) via WhatsApp / SMS / email
            </li>
            <li>To process payments and prevent fraud</li>
            <li>To provide customer support</li>
            <li>To comply with legal obligations (tax, accounting, lawful requests)</li>
            <li>
              To send you product updates and marketing about Wamerce (you can unsubscribe at any
              time)
            </li>
          </ul>

          <h2>4. Legal Basis (GDPR / DPDP)</h2>
          <p>
            We process personal data on the basis of: (a) <strong>contract</strong> — to provide
            the Service you signed up for; (b) <strong>legitimate interest</strong> — for security,
            fraud prevention and product improvement; (c) <strong>consent</strong> — for marketing
            messages to end customers; and (d) <strong>legal obligation</strong> — to comply with
            applicable laws.
          </p>

          <h2>5. Sharing & Sub-processors</h2>
          <p>We share data only with trusted vendors who help us deliver the Service:</p>
          <ul>
            <li>
              <strong>Meta Platforms, Inc.</strong> — WhatsApp Cloud API & Conversions API
            </li>
            <li>
              <strong>Shopify Inc.</strong> — your connected store's data
            </li>
            <li>
              <strong>MongoDB Atlas</strong> — primary database
            </li>
            <li>
              <strong>Cloudflare</strong> — DDoS protection, CDN
            </li>
            <li>
              <strong>PostHog</strong> — product analytics (anonymised events)
            </li>
            <li>
              <strong>Razorpay / Stripe</strong> — payment processing
            </li>
            <li>
              <strong>Logistics partners</strong> (Shri Maruti / Innofulfill, TCS, etc.) — only
              shipping address & contact for orders you push
            </li>
          </ul>
          <p>
            We do <strong>not</strong> sell personal data. We do not share data with advertisers
            beyond conversion events you explicitly enable (e.g., Meta CAPI).
          </p>

          <h2>6. Data Retention</h2>
          <ul>
            <li>
              <strong>Account data:</strong> retained while your account is active, plus 7 years
              for accounting/tax compliance.
            </li>
            <li>
              <strong>WhatsApp messages:</strong> retained for 24 months, then automatically
              deleted unless you export or extend retention from your dashboard.
            </li>
            <li>
              <strong>Analytics events:</strong> 13 months rolling window.
            </li>
            <li>
              <strong>Webhook event logs:</strong> 90 days.
            </li>
          </ul>

          <h2>7. Your Rights</h2>
          <p>Subject to applicable law, you have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate data</li>
            <li>Delete your account and associated data (right to be forgotten)</li>
            <li>Export your data in machine-readable format</li>
            <li>Object to or restrict certain processing</li>
            <li>Withdraw consent at any time</li>
            <li>Lodge a complaint with your local supervisory authority</li>
          </ul>
          <p>
            To exercise any of these rights, email{' '}
            <a href="mailto:privacy@wamerce.com">privacy@wamerce.com</a>. We respond within 30 days.
          </p>

          <h2>8. Data Deletion</h2>
          <p>
            You can delete your account at any time from{' '}
            <strong>Settings → Account → Delete Account</strong>. Facebook / Meta users may also
            request data deletion at{' '}
            <Link to="/data-deletion" className="text-emerald-400 hover:underline">
              wamerce.com/data-deletion
            </Link>
            .
          </p>

          <h2>9. Security</h2>
          <p>
            We use TLS 1.3 in transit, AES-256 at rest for sensitive fields (API tokens, access
            tokens, OAuth credentials), bcrypt for password hashing, principle-of-least-privilege
            access controls, and quarterly security reviews. No system is 100% secure; we will
            notify affected users within 72 hours of any confirmed breach.
          </p>

          <h2>10. Children</h2>
          <p>
            The Service is not directed to children under 18. We do not knowingly collect data from
            minors.
          </p>

          <h2>11. International Transfers</h2>
          <p>
            Wamerce is operated from India. By using the Service you consent to transfer of your
            data to India and any other country where our sub-processors operate, under appropriate
            safeguards (Standard Contractual Clauses where required).
          </p>

          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this policy from time to time. Material changes will be notified by email
            and in-app at least 14 days before they take effect. The “Last updated” date above
            reflects the current version.
          </p>

          <h2>13. Contact</h2>
          <p>
            Data Controller: <strong>Wamerce</strong>
            <br />
            Email: <a href="mailto:privacy@wamerce.com">privacy@wamerce.com</a>
            <br />
            Grievance Officer (India / DPDP):{' '}
            <a href="mailto:grievance@wamerce.com">grievance@wamerce.com</a>
          </p>
        </article>

        <footer className="mt-16 pt-8 border-t border-zinc-800/60 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-500">
          <p>© {new Date().getFullYear()} Wamerce. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" className="hover:text-white">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-white">
              Terms
            </Link>
            <Link to="/data-deletion" className="hover:text-white">
              Data deletion
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
